/**
 * Authentication Context
 * Manages authentication state and provides auth methods
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
// import cognitoAuthService, { AuthUser } from '../services/cognito-auth.service';
import localAuthService, { AuthUser } from '../services/local-auth.service';

export interface LoginCredentials {
  email: string;
  password: string;
}

export type User = AuthUser;

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => void;
  verifyAuth: () => Promise<boolean>;
  clearError: () => void;
  // Session timeout methods
  refreshToken: () => Promise<boolean>;
  updateActivity: () => void;
  getSessionInfo: () => { sessionId: string | null; lastActivity: Date | null };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Session management state
  const [lastActivity, setLastActivity] = useState<Date>(new Date());
  const [sessionId] = useState<string>(() => `session_${Date.now()}_${Math.random()}`);

  /**
   * Verify authentication status on mount with Cognito
   */
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        // Check if user is authenticated with local backend
        const currentUser = await localAuthService.getCurrentUser();

        if (currentUser) {
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          // No authenticated user
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        setIsAuthenticated(false);
        setUser(null);
        setError('Failed to initialize authentication');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Login function
   */
  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await localAuthService.signIn(credentials.email, credentials.password);

      if (result && result.isSignInComplete && result.user) {
        setUser(result.user);
        setIsAuthenticated(true);
        setError(null);
        return true;
      } else {
        setError('Login failed');
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'An error occurred during login');
      setIsAuthenticated(false);
      setUser(null);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    try {
      await localAuthService.signOut();
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
      // Still clear local state even if signOut fails
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    }
  }, []);

  /**
   * Verify authentication
   */
  const verifyAuth = useCallback(async (): Promise<boolean> => {
    try {
      const isAuth = localAuthService.isAuthenticated();

      if (!isAuth) {
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }

      const currentUser = await localAuthService.getCurrentUser();

      if (currentUser) {
        setUser(currentUser);
        setIsAuthenticated(true);
        return true;
      } else {
        await localAuthService.signOut();
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }
    } catch (err) {
      console.error('Auth verification error:', err);
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  }, []);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Update activity timestamp
   */
  const updateActivity = useCallback(() => {
    setLastActivity(new Date());
  }, []);

  /**
   * Get session information
   */
  const getSessionInfo = useCallback(() => {
    return {
      sessionId,
      lastActivity
    };
  }, [sessionId, lastActivity]);

  /**
   * Refresh token (placeholder for session extension)
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      // For local auth, we can verify the current session
      const isAuth = localAuthService.isAuthenticated();
      if (isAuth) {
        updateActivity();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, [updateActivity]);

  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    verifyAuth,
    clearError,
    refreshToken,
    updateActivity,
    getSessionInfo,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;